import dayjs from 'dayjs';
import { Job } from '../generated/gql-types';
import {
  computeShiftDurationHours,
  computeShiftDurationHoursFromUnixMs,
  formatDurationHours,
} from './datetime.util';
import { formatUnixMsAsLocalHHmm, isUnixMsTimestamp } from './jobTimezone.util';
import { parseJobDayClockTime } from './jobScheduleBounds.util';

const RANGE_SEPARATOR = ' - ';

/** When {@link formatJobDaysSchedule} cannot read `job.days`. */
export const DEFAULT_SCHEDULE_TIME_LABEL = '09:00 - 17:00 (8.00 hrs)';

/**
 * `17:00 – 23:45 (6.75 hrs)` — shared presentation for schedule and logged times.
 */
export function formatShiftRangeLabel(
  startHHmm: string,
  endHHmm: string,
  durationHours?: number,
): string {
  const range = `${startHHmm}${RANGE_SEPARATOR}${endHHmm}`;
  if (durationHours === undefined || !Number.isFinite(durationHours)) {
    return range;
  }
  return `${range} (${formatDurationHours(durationHours)} hrs)`;
}

/**
 * Renders scheduled shift times from `job.days`, including overnight shifts
 * (end time on the calendar day after start, e.g. 23:45 – 02:45).
 */
export function formatJobDaysSchedule(
  job: Pick<Job, 'days'> | null | undefined,
): string {
  const jobDays = job?.days;
  if (!jobDays?.startTime || !jobDays?.endTime) {
    return 'Not set';
  }

  const startParts = parseJobDayClockTime(jobDays.startTime);
  const endParts = parseJobDayClockTime(jobDays.endTime);

  if (!startParts || !endParts) {
    return 'Not set';
  }

  const start = dayjs()
    .hour(startParts.hour)
    .minute(startParts.minute)
    .second(0)
    .millisecond(0);

  const end = dayjs()
    .hour(endParts.hour)
    .minute(endParts.minute)
    .second(0)
    .millisecond(0);

  if (!start.isValid() || !end.isValid()) {
    return 'Not set';
  }

  return formatShiftRangeLabel(
    start.format('HH:mm'),
    end.format('HH:mm'),
    computeShiftDurationHours(start, end),
  );
}

function formatTimestampOrNotSet(
  timestamp: string | null | undefined,
  _jobOrTimezone?: Pick<Job, 'timezone'> | string | null,
): string {
  if (!timestamp) {
    return 'Not set';
  }
  if (!isUnixMsTimestamp(timestamp)) {
    return 'Not set';
  }
  const formatted = formatUnixMsAsLocalHHmm(timestamp);
  return formatted || 'Not set';
}

/**
 * Logged or proposed clock times (unix ms) as local `HH:mm` (matches form round-trip).
 */
export function formatTimeRangeAndDuration(
  clockIn?: string | null,
  clockOut?: string | null,
  jobOrTimezone?: Pick<Job, 'timezone'> | string | null,
): string {
  if (!clockIn && !clockOut) {
    return 'Not set';
  }

  const startTime = formatTimestampOrNotSet(clockIn, jobOrTimezone);
  const endTime = formatTimestampOrNotSet(clockOut, jobOrTimezone);

  if (!clockIn || !clockOut) {
    return formatShiftRangeLabel(startTime, endTime);
  }

  const durationHours = computeShiftDurationHoursFromUnixMs(clockIn, clockOut);
  return formatShiftRangeLabel(startTime, endTime, durationHours);
}
